import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { GameData, InputType, LinkType, RelicData, View } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponZodiacNovusProps } from "views/relics/weapons/zodiac/novus/types";
import "./WeaponZodiacNovus.css";

const mapStateToProps = (state:AppState) => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class WeaponZodiacNovus extends Component<ViewWeaponZodiacNovusProps> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_ZODIAC,
		part: RelicData.Part.WEAPON_NOVUS,
		steps: [RelicData.Step.RADIANCE, RelicData.Step.SCROLL, RelicData.Step.STAR, RelicData.Step.ALEXANDRITE, RelicData.Step.BINDING],
		stepPlaceholders: {
			[RelicData.Step.RADIANCE]: { QUEST: GameData.Quest.CELESTIAL_RADIANCE },
			[RelicData.Step.SCROLL]: { ITEM: "Sphere Scroll" },
			[RelicData.Step.STAR]: { QUEST: GameData.Quest.STAR_LIGHT_STAR_BRIGHT },
			[RelicData.Step.ALEXANDRITE]: { ITEM: "Alexandrite" },
		},
		next: {
			part: RelicData.Part.WEAPON_NEXUS,
			view: View.RELIC_WEAPONS_ZODIAC_NEXUS,
		},
		previous: {
			part: RelicData.Part.WEAPON_ANIMUS,
			view: View.RELIC_WEAPONS_ZODIAC_ANIMUS,
		},
	};

	constructor(props:ViewWeaponZodiacNovusProps){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	
	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);
		const scrollInventory = manager.data.getInventoryStatus({
			item: manager.relics.getTaskItem(RelicData.Step.SCROLL, RelicData.Task.TASK_1),
			relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SCROLL, RelicData.Task.TASK_1],
		});
		const alexandriteInventory = manager.data.getInventoryStatus({
			item: manager.relics.getTaskItem(RelicData.Step.ALEXANDRITE, RelicData.Task.TASK_1),
			relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.ALEXANDRITE, RelicData.Task.TASK_1],
		});

		return (
			<div id="weapon-zodiac-novus" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
					<p>{t.t("The %PART% upgrade follows on from %PREVIOUSPART%", { PART: this.relicInfo.part, PREVIOUSPART: this.relicInfo.previous ? this.relicInfo.previous.part : "Unknown"})}</p>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={RelicData.Step.RADIANCE} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.RADIANCE]} id={RelicData.Step.RADIANCE} stepComplete={completeSteps[RelicData.Step.RADIANCE]}><p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={quests[GameData.Quest.CELESTIAL_RADIANCE].type} text={quests[GameData.Quest.CELESTIAL_RADIANCE].name} lodestoneID={quests[GameData.Quest.CELESTIAL_RADIANCE].lodestoneID} /></p></DetailsSection>
					<DetailsSection title={RelicData.Step.SCROLL} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.SCROLL]} id={RelicData.Step.SCROLL} stepComplete={completeSteps[RelicData.Step.SCROLL]}>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity / Job")}</th><th>{t.t("Total")}</th><th>{t.t("Used")}</th><th>{t.t("Remaining")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
								<tbody>
									{this.props.hideCompletedTasks && scrollInventory.toObtain === 0
										? null
										: <tr className="odd-row">
											<td><Obtained obtained={items[GameData.Item.SUPERIOR_ENCHANTED_INK].obtained} qtyNeeded={scrollInventory.total} /></td>
											<td>{t.t(items[GameData.Item.SUPERIOR_ENCHANTED_INK].name)}</td>
											<td>{scrollInventory.qtyPerJob.toLocaleString()}</td>
											<td>{scrollInventory.total.toLocaleString()}</td>
											<td>{scrollInventory.used.toLocaleString()}</td>
											<td>{scrollInventory.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: scrollInventory.inventory, dataAttributes: { item: GameData.Item.SUPERIOR_ENCHANTED_INK }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
											<td>{scrollInventory.toObtain.toLocaleString()}</td>
										</tr>
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SCROLL)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
					<DetailsSection title={RelicData.Step.STAR} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.STAR]} id={RelicData.Step.STAR} stepComplete={completeSteps[RelicData.Step.STAR]}><p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={quests[GameData.Quest.STAR_LIGHT_STAR_BRIGHT].type} text={quests[GameData.Quest.STAR_LIGHT_STAR_BRIGHT].name} lodestoneID={quests[GameData.Quest.STAR_LIGHT_STAR_BRIGHT].lodestoneID} /></p></DetailsSection>
					<DetailsSection title={RelicData.Step.ALEXANDRITE} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.ALEXANDRITE]} id={RelicData.Step.ALEXANDRITE} stepComplete={completeSteps[RelicData.Step.ALEXANDRITE]}>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity / Job")}</th><th>{t.t("Total")}</th><th>{t.t("Used")}</th><th>{t.t("Remaining")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
								<tbody>
									{this.props.hideCompletedTasks && alexandriteInventory.toObtain === 0
										? null
										: <tr className="odd-row">
											<td><Obtained obtained={items[GameData.Item.ALEXANDRITE].obtained} qtyNeeded={alexandriteInventory.total} /></td>
											<td>{t.t(items[GameData.Item.ALEXANDRITE].name)}</td>
											<td>{alexandriteInventory.qtyPerJob.toLocaleString()}</td>
											<td>{alexandriteInventory.total.toLocaleString()}</td>
											<td>{alexandriteInventory.used.toLocaleString()}</td>
											<td>{alexandriteInventory.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: alexandriteInventory.inventory, dataAttributes: { item: GameData.Item.ALEXANDRITE }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
											<td>{alexandriteInventory.toObtain.toLocaleString()}</td>
										</tr>
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.ALEXANDRITE)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
					<DetailsSection title={RelicData.Step.BINDING} id={RelicData.Step.BINDING} stepComplete={completeSteps[RelicData.Step.BINDING]}>
						<p>{t.t("This step requires 75 of various Grade I-IV materia along with the Alexandrite and Sphere Scroll from the previous steps")}</p>
						<p>{t.t("The materia you choose will effect the stats on the created weapon")}</p>
						<p>{t.t("You can track your progress through the Sphere Scroll")}</p>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount(){
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }
		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.JALZAHN, GameData.NPC.HUBAIRTIN];
	}
}
export default connect(mapStateToProps)(WeaponZodiacNovus);
